// Stock admin (#259) — the read (`GET /api/admin/stock` -> web-bff ->
// inventory-service's admin Lambda, `BatchGetItem` over the catalog's own
// SKU list) and the write, restock (`POST /api/admin/restock` -> web-bff
// -> the same Lambda, ADR 0063). The read used to go through
// load-generator's `GET /stock` Scan (ADR 0046) — moved onto
// inventory-service itself in #262 (ADR 0064) once load-generator-ui, the
// only other caller of that Scan, was retired.
//
// Same fetch-wrapper shape as catalog.ts's `call<T>()` — duplicated, not
// imported, per this repo's "no shared JS package across services/screens"
// convention (screens are independent files even within one service).

export interface StockItem {
  sku: string;
  name?: string;
  quantityOnHand: number;
  initialStock?: number;
}

export interface RestockedItem {
  sku: string;
  previousQuantity: number;
  quantity: number;
}

export interface RestockResult {
  count: number;
  items: RestockedItem[];
  skipped: string[];
}

export type StockLevel = "out" | "low" | "ok";

/**
 * Ported from load-generator-ui's StockPage.tsx (ADR 0046) — out at 0,
 * low under 10% of the catalog initialStock (or under 20 units when
 * initialStock isn't known), otherwise ok.
 */
export function stockLevel(item: StockItem): StockLevel {
  if (item.quantityOnHand <= 0) return "out";
  const threshold = item.initialStock ? item.initialStock * 0.1 : 20;
  return item.quantityOnHand < threshold ? "low" : "ok";
}

export type StockResult<T> =
  | { kind: "ok"; value: T }
  | { kind: "error"; message: string }
  // No session — the caller should send the visitor to sign in.
  | { kind: "unauthorized" };

async function call<T>(
  input: RequestInfo,
  init: RequestInit | undefined,
  unwrap: (payload: unknown) => T | undefined,
): Promise<StockResult<T>> {
  let res: Response;
  try {
    res = await fetch(input, { credentials: "include", ...init });
  } catch (err) {
    return { kind: "error", message: err instanceof Error ? err.message : "request failed (network)" };
  }

  if (res.status === 401) {
    return { kind: "unauthorized" };
  }

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const apiError =
      payload && typeof payload === "object" && "error" in payload
        ? String((payload as { error: unknown }).error)
        : `${res.status} ${res.statusText}`.trim();
    return { kind: "error", message: apiError };
  }

  const value = unwrap(payload);
  if (value === undefined) {
    return { kind: "error", message: "unexpected response shape" };
  }
  return { kind: "ok", value };
}

export function fetchStock(): Promise<StockResult<StockItem[]>> {
  return call<StockItem[]>("/api/admin/stock", undefined, (p) =>
    p && typeof p === "object" && "items" in p ? (p as { items: StockItem[] }).items : undefined,
  );
}

function restock(body: Record<string, unknown>): Promise<StockResult<RestockResult>> {
  return call<RestockResult>(
    "/api/admin/restock",
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    },
    (p) =>
      p && typeof p === "object" && "items" in p && "skipped" in p ? (p as RestockResult) : undefined,
  );
}

/** Reset one SKU to its catalog initialStock. */
export function restockOne(sku: string): Promise<StockResult<RestockResult>> {
  return restock({ sku });
}

/** Reset every catalog SKU to its initialStock. */
export function restockAll(): Promise<StockResult<RestockResult>> {
  return restock({});
}
